import { useEffect } from 'react';
import { Hero, SocialProof, Testimonials1, Problem } from './components/HeaderSections';
import { ProductTomos, Bonuses, UrgencyCounter, Pricing } from './components/CoreSections';
import { Testimonials2, Guarantee, FAQ, CostOfNotHaving, FinalCTA, Footer } from './components/FooterSections';
import { AnnouncementBar } from './components/AnnouncementBar';
import { StickyMobileCTA } from './components/StickyMobileCTA';
import { SocialProofPopup } from './components/SocialProofPopup';
import { ExitIntentModal } from './components/ExitIntentModal';
import { MetaPixel } from './components/MetaPixel';
// Helper: ensure a <meta> tag with given attributes exists in <head>
const ensureMeta = (attrs: Record<string, string>) => {
  if (typeof document === 'undefined') return;
  const selector = Object.entries(attrs).filter(([k]) => k !== 'content').map(([k, v]) => `[${k}="${v}"]`).join('');
  let meta = document.head.querySelector<HTMLMetaElement>(`meta${selector}`);
  if (!meta) {
    meta = document.createElement('meta');
    document.head.appendChild(meta);
  }
  Object.entries(attrs).forEach(([k, v]) => meta!.setAttribute(k, v));
};
// Helper: ensure a <link> tag exists in <head>
const ensureLink = (attrs: Record<string, string>) => {
  if (typeof document === 'undefined') return;
  const selector = `link[rel="${attrs.rel}"][href="${attrs.href}"]`;
  if (document.head.querySelector(selector)) return;
  const link = document.createElement('link');
  Object.entries(attrs).forEach(([k, v]) => link.setAttribute(k, v));
  document.head.appendChild(link);
};
export function App() {
  useEffect(() => {
    if (typeof document === 'undefined') return;
    // Language + translation blocking (Spanish - Latin America)
    document.documentElement.lang = 'es-419';
    document.documentElement.setAttribute('translate', 'no');
    document.title = 'MEGAPACK Funcional 365 — Todo tu año de funcional, listo';
    ensureMeta({
      'http-equiv': 'content-language',
      content: 'es-419'
    });
    ensureMeta({
      name: 'google',
      content: 'notranslate'
    });
    ensureMeta({
      name: 'language',
      content: 'Spanish'
    });
    ensureMeta({
      name: 'description',
      content: 'MEGAPACK Funcional 365: 6 Tomos, 365 sesiones, 5 bonos estratégicos. Todo tu año de clases de funcional, listo desde hoy por 17 USD. Pagas en tu moneda local.'
    });
    ensureMeta({
      name: 'theme-color',
      content: '#020617'
    });
    ensureMeta({
      name: 'viewport',
      content: 'width=device-width, initial-scale=1, viewport-fit=cover'
    });
    // Performance: preconnect to Google Fonts (faster font load)
    ensureLink({
      rel: 'preconnect',
      href: 'https://fonts.googleapis.com'
    });
    ensureLink({
      rel: 'preconnect',
      href: 'https://fonts.gstatic.com',
      crossorigin: ''
    });
    // Performance: preconnect to the CDN serving the hero mockup
    ensureLink({
      rel: 'preconnect',
      href: 'https://cdn.magicpatterns.com'
    });
    ensureLink({
      rel: 'preload',
      as: 'image',
      href: 'https://cdn.magicpatterns.com/uploads/a5wDcjcYkyBHi6BisQcSi3/MOCKUP_PNG.png',
      // @ts-ignore
      fetchpriority: 'high'
    });
    // Performance: warm up the checkout connection so CTA clicks navigate
    // instantly. TCP + TLS happens in the background while the user reads.
    ensureLink({
      rel: 'dns-prefetch',
      href: 'https://megapack-funcional-365.impultienda.ar'
    });
    // Page navigations use a non-CORS connection, so no `crossorigin` here —
    // otherwise the warmed-up connection would be ignored by the checkout.
    ensureLink({
      rel: 'preconnect',
      href: 'https://megapack-funcional-365.impultienda.ar'
    });
    // Once the page is idle, quietly prefetch the checkout page so it opens
    // almost instantly when the visitor taps a CTA.
    const prefetchCheckout = () => ensureLink({
      rel: 'prefetch',
      href: 'https://megapack-funcional-365.impultienda.ar/checkout'
    });
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: {
        timeout: number;
      }) => number;
    };
    if (w.requestIdleCallback) w.requestIdleCallback(prefetchCheckout, {
      timeout: 4000
    });else window.setTimeout(prefetchCheckout, 3000);
    // Performance: open the pixel connection early
    ensureLink({
      rel: 'preconnect',
      href: 'https://connect.facebook.net'
    });
  }, []);
  return <div lang="es-419" translate="no" className="notranslate min-h-screen bg-slate-950 font-sans text-slate-300 selection:bg-sky-400 selection:text-slate-950" data-id="element-0" data-mp="14x6jxlm8k9tn">
      <MetaPixel data-id="element-1" data-mp="vw8o3qlyb8m5" />
      <SocialProofPopup data-id="element-2" data-mp="vwrt66qi62ir" />
      <ExitIntentModal data-id="element-3" data-mp="vvgq694vsysp" />
      <StickyMobileCTA data-id="element-4" data-mp="vvyxq1wzuzfx" />
      <AnnouncementBar data-id="element-5" data-mp="3ko2r4muc2yu3" />
      <Hero data-id="element-6" data-mp="3koknrkkwrf2j" />
      <SocialProof data-id="element-7" data-mp="3kp42ksf9hncc" />
      <Testimonials1 data-id="element-8" data-mp="3kpm70cc2v7vc" />
      <Problem data-id="element-9" data-mp="3klyglm1xj1ku" />
      <ProductTomos data-id="element-10" data-mp="3gmh8ekv4bqbz" />
      <Bonuses data-id="element-11" data-mp="3gm7s5ozvkxjh" />
      <UrgencyCounter data-id="element-12" data-mp="3glekmlt3e40a" />
      <Pricing data-id="element-13" data-mp="3gl62ofi448i8" />
      <Testimonials2 data-id="element-14" data-mp="3gotyjlb15704" />
      <Guarantee data-id="element-15" data-mp="1w4ov8gm37sve" />
      <FAQ data-id="element-16" data-mp="1w581v16aeuu4" />
      <CostOfNotHaving data-id="element-17" data-mp="1w5ghv1xpig9j" />
      <FinalCTA data-id="element-18" data-mp="1w69n1yh0oxpk" />
      <Footer data-id="element-19" data-mp="1w2lvh4v4uldh" />
    </div>;
}